import { useRef, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector, useTexts } from "../../hooks";
import {
  getClasses,
  hasTouch,
  splitNewLines,
  doTranslation,
  dispatchEvent,
} from "../../utils";
import { selectButton } from "../../store/slices/buttons";
import classes from "../../styles/BetBoost.module.css";
import { toggleBetBoost, setTooltip } from "../../store/slices/bet-boost";
import Button from "../buttons/Button";
import { EVENTS } from "../../commander/Types";

const BetBoost: React.FC<{ className?: string }> = (props) => {
  const dispatch = useDispatch();
  const container = useRef(null);
  const { available, disabled, hidden, state, autoplay, lang, visible } =
    useAppSelector((s) => ({
      ...selectButton("betBoost", s),
      state: s.betBoost,
      autoplay: s.autoplay,
      lang: s.details.language,
      visible: s.details.visible,
    }));

  const [clicked, setClicked] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const { common, custom } = useTexts();

  useEffect(() => {
    if (clicked) {
      const delay = 300;
      const interval = setTimeout(() => setClicked(false), delay);

      return () => clearInterval(interval);
    }
  }, [clicked]);

  const onClick = () => {
    if (clicked) return;

    const value = !!state && !state.active;
    dispatch(toggleBetBoost(value));
    const eventData = value
      ? { multiplier: state?.value, action: state.action }
      : null;
    dispatchEvent(EVENTS.BET_BOOST, eventData);
    dispatch(setTooltip(value));
  };

  const onHover = (value: boolean) => {
    if (hasTouch() || disabled || autoplay.active) return;

    dispatch(setTooltip(value));
    setInitialized(true);
  };

  const tooltipText = Object.keys({ ...custom, ...common })
    // TODO extract the "tooltipbetboost" to some constant with the other
    // similar kyes /like the ones for the buy bonus/
    .filter((key) => key.includes("tooltipbetboost"))
    .map((key) => splitNewLines(doTranslation(key, custom, common)));

  const initializedClass = initialized ? "initialized" : "";
  const activeClass = !!state && state.active ? "active" : "";
  const touchClass = hasTouch() ? "touch" : "";
  const textClass = lang === "en_sc" ? "text-social" : "text";

  useEffect(() => {
    const onTap = (e: any) => {
      // if the UI is visible and the click is outside of the button then the initial tooltip is hidden
      if (
        visible &&
        container.current &&
        !(container.current as HTMLElement).contains(e.target)
      ) {
        dispatch(setTooltip(false));
      }
    };
    window.addEventListener("touchstart", onTap);

    return () => window.removeEventListener("touchstart", onTap);
  });

  if (state === null || state.value === 0) return null;

  return (
    <div
      className={getClasses(
        classes,
        "bet-boost",
        activeClass,
        touchClass,
        initializedClass,
        props.className
      )}
      ref={container}
    >
      <Button
        className={getClasses(classes)}
        available={available}
        disabled={disabled || autoplay.active}
        hidden={hidden}
        onClick={onClick}
        animations={{ click: { duration: 0 } }}
        onHover={onHover.bind(null, true)}
        onHoverOut={onHover.bind(null, false)}
      >
        <div>
          <div className={classes["base-outer"]}>
            <div className={classes["base"]}>
              <div className={classes["shadow"]}></div>
              <div className={classes["lightning-backlight"]}></div>
              <div className={classes["lightning-backlight-active"]}></div>
              <div className={classes["lightning"]}></div>
              <div className={classes["lightning-active"]}></div>
              <div className={getClasses(classes, textClass)}></div>
            </div>
          </div>
          <div className={classes["switch-base"]}>
            <div className={classes["switch-bar"]}></div>
            <div className={classes["switch-head"]}></div>
          </div>
        </div>
        <div
          className={getClasses(
            classes,
            "tooltip",
            state.tooltip ? "visible" : ""
          )}
        >
          {tooltipText}
        </div>
      </Button>
    </div>
  );
};

export default BetBoost;
